import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import type { LocalSeoData } from '@/mocks/localSeoData';

interface LocalSeoPageProps {
  data: LocalSeoData;
}

function FaqSection({ faq }: { faq: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="relative py-20 bg-gp-graphite-light" id="faq">
      <div className="px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-[3px] bg-gp-yellow"></div>
            <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
              Perguntas Frequentes
            </h2>
          </div>

          <div className="space-y-3">
            {faq.map((item, index) => (
              <div
                key={index}
                className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-white font-medium text-sm md:text-base pr-4">
                    {item.question}
                  </span>
                  <i
                    className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-gp-yellow w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300`}
                  ></i>
                </button>
                {openIndex === index && (
                  <div className="px-5 pb-4 border-t border-gp-graphite-lighter/50">
                    <p className="text-gray-400 text-sm leading-relaxed pt-4">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LocalSeoPage({ data }: LocalSeoPageProps) {
  useSeo({
    title: data.title,
    description: data.description,
    keywords: data.keywords,
    canonical: data.canonical,
    ogUrl: data.ogUrl,
    ogImage: data.ogImage,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={data.imagemHero}
              alt={`${data.serviceName} em ${data.city} - Grupo Pilar`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
                  {data.serviceCategory}
                </span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">{data.serviceName.split(' ').slice(0, 2).join(' ')}</span>{' '}
                {data.serviceName.split(' ').slice(2).join(' ')} em {data.city}
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                {data.description}
              </p>
            </div>
          </div>
        </section>

        {/* Introdução Local */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                  Atuação da Pilar em {data.city}
                </h2>
              </div>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                {data.introLocal.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Imagem de Conteúdo */}
        <section className="relative py-10">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src={data.imagemConteudo}
                  alt={`${data.serviceName} - ${data.city} - Grupo Pilar`}
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gp-graphite/20"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Problemas Enfrentados */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                  Principais Problemas Enfrentados pelas Indústrias de {data.city}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.problemas.map((problem, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300"
                  >
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{problem}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Como Executamos */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                  Como a Pilar Executa {data.serviceName}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.comoExecutamos.map((step, index) => (
                  <div
                    key={index}
                    className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                  >
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                      <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Segmentos Atendidos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                  Segmentos Industriais Atendidos em {data.city} e Região
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.segmentos.map((segment, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{segment}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                  Diferenciais da Pilar
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.diferenciais.map((diferencial, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{diferencial}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FaqSection faq={data.faq} />

        {/* CTA Comercial */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
                <div className="absolute inset-0 bg-gp-graphite/60"></div>
                <div className="relative z-10 p-8 md:p-12 text-center">
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                    {data.ctaTitle}
                  </h2>
                  <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                    {data.ctaDesc}
                  </p>
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>{data.ctaButtonText}</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Linkagem Interna */}
        <section className="relative py-16 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-lg md:text-xl font-bold uppercase tracking-wider">
                  Links Relacionados
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link
                  to={data.linkPrincipal}
                  className="group flex items-center gap-3 bg-gp-graphite border border-gp-graphite-lighter rounded-lg px-5 py-4 hover:border-gp-yellow/40 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className="ri-tools-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-300 text-sm font-medium group-hover:text-gp-yellow transition-colors duration-300 block">
                      {data.linkPrincipalLabel}
                    </span>
                  </div>
                </Link>

                <Link
                  to="/contato"
                  className="group flex items-center gap-3 bg-gp-graphite border border-gp-graphite-lighter rounded-lg px-5 py-4 hover:border-gp-yellow/40 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className="ri-mail-send-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-300 text-sm font-medium group-hover:text-gp-yellow transition-colors duration-300 block">
                      Página de Contato
                    </span>
                  </div>
                </Link>

                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 bg-gp-graphite border border-gp-graphite-lighter rounded-lg px-5 py-4 hover:border-gp-yellow/40 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-300 text-sm font-medium group-hover:text-gp-yellow transition-colors duration-300 block">
                      Solicitar Orçamento via WhatsApp
                    </span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}